import useTranslate from "@hooks/use_translate"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import {
  hasDiff,
  summarizeDiff,
  type DiffSummary
} from "@pages/main/scripts/prompts/sub_components/diffSummary"
import type { ReactNode } from "react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import DiffViewer from "react-diff-viewer-continued"

const STAT_BLOCKS = 5

/** GitHub-style `+12 −3 ▮▮▮▯▯`: the size of a change readable without opening it. */
export function DiffStat({
  summary,
  showBlocks = true
}: {
  summary: DiffSummary
  showBlocks?: boolean
}) {
  const { translate } = useTranslate()

  if (!hasDiff(summary)) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        {translate("prompt_diff_no_changes")}
      </Typography>
    )
  }

  const total = summary.additions + summary.deletions
  const green = Math.round((summary.additions / total) * STAT_BLOCKS)

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 700 }}
    >
      <Box component="span" sx={{ color: "success.main" }}>
        +{summary.additions}
      </Box>
      <Box component="span" sx={{ color: "error.main" }}>
        −{summary.deletions}
      </Box>
      {showBlocks && (
        <Stack direction="row" spacing={0.25}>
          {Array.from({ length: STAT_BLOCKS }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: 0.25,
                bgcolor: i < green ? "success.main" : "error.main"
              }}
            />
          ))}
        </Stack>
      )}
      <Typography
        component="span"
        variant="caption"
        sx={{ color: "text.secondary", fontFamily: "inherit" }}
      >
        {translate("prompt_diff_hunks", { count: summary.hunks.length })}
      </Typography>
    </Stack>
  )
}

interface PromptDiffPanelProps {
  title: ReactNode
  oldValue: string
  newValue: string
  defaultExpanded?: boolean
  focusFirstChange?: boolean
}

/**
 * One diffed section with a sticky header carrying its stats and ↑/↓ change navigation. The
 * header sticks to the dialog's scroll container, so the arrows stay reachable in a long prompt.
 */
export default function PromptDiffPanel({
  title,
  oldValue,
  newValue,
  defaultExpanded = true,
  focusFirstChange = false
}: PromptDiffPanelProps) {
  const { translate } = useTranslate()
  const summary = useMemo(
    () => summarizeDiff(oldValue, newValue),
    [oldValue, newValue]
  )
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [current, setCurrent] = useState(-1)
  const [marker, setMarker] = useState<{ top: number; height: number } | null>(
    null
  )
  const bodyRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setCurrent(focusFirstChange && summary.hunks.length > 0 ? 0 : -1)
  }, [summary, focusFirstChange])

  useLayoutEffect(() => {
    const hunk = summary.hunks[current]
    const body = bodyRef.current
    if (!hunk || !body || !expanded) {
      setMarker(null)
      return
    }

    const measure = () => {
      const rows =
        body.querySelectorAll<HTMLTableRowElement>("table > tbody > tr")
      const first = rows[hunk.startRow]
      const last = rows[hunk.endRow]
      if (!first || !last) return
      const origin = body.getBoundingClientRect().top
      const top = first.getBoundingClientRect().top - origin
      setMarker({
        top,
        height: last.getBoundingClientRect().bottom - origin - top
      })
      return first
    }

    // Right after expanding, the collapse is still animating and the rows are not laid out yet.
    const timer = window.setTimeout(() => {
      measure()?.scrollIntoView({ block: "center", behavior: "smooth" })
    }, 0)
    const observer = new ResizeObserver(() => measure())
    observer.observe(body)
    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
  }, [current, expanded, summary])

  const count = summary.hunks.length
  const step = (delta: number) => {
    if (!count) return
    setExpanded(true)
    setCurrent((prev) =>
      prev === -1 ? (delta > 0 ? 0 : count - 1) : (prev + delta + count) % count
    )
  }

  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={() => setExpanded((open) => !open)}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          px: 1.5,
          py: 0.75,
          cursor: "pointer",
          bgcolor: "background.paper",
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
          borderTopLeftRadius: "inherit",
          borderTopRightRadius: "inherit"
        }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transition: "transform 150ms",
            transform: expanded ? "none" : "rotate(-90deg)",
            color: "text.secondary"
          }}
        />
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <DiffStat summary={summary} />
        <Box sx={{ flex: 1 }} />
        {count > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            onClick={(event) => event.stopPropagation()}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontFamily: "monospace",
                minWidth: 44,
                textAlign: "right",
                mr: 0.5
              }}
            >
              {current === -1 ? `– / ${count}` : `${current + 1} / ${count}`}
            </Typography>
            <Tooltip title={translate("prompt_diff_prev_change")}>
              <IconButton size="small" onClick={() => step(-1)}>
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate("prompt_diff_next_change")}>
              <IconButton size="small" onClick={() => step(1)}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box ref={bodyRef} sx={{ position: "relative" }}>
          <DiffViewer
            oldValue={oldValue}
            newValue={newValue}
            splitView
            useDarkTheme={false}
            hideLineNumbers={false}
            showDiffOnly={false}
          />
          {marker && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: marker.top,
                height: marker.height,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 0.5,
                pointerEvents: "none"
              }}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}
